/**
 * "Mobile app" — the user's own login QR (#1244 slice E6, design §5.4, G6).
 *
 * The QR this renders is **a password equivalent for 90 seconds**: whoever scans
 * it first gets a full mobile session as this user. The threat is not a remote
 * attacker, it is a phone camera pointed at a shop-floor monitor or a screenshot
 * pasted into a group chat. Every rule below follows from that:
 *
 * - **Reveal on demand, never on open.** A profile dialog left open on a shared PC
 *   must not be displaying a live credential to the room, so nothing is minted
 *   until the user asks and proves who they are.
 * - **Password step-up every time.** Not a UX nicety — the server requires it on
 *   every mint (there is no bypass and no "remember for this session"), so the
 *   form matches the endpoint rather than trying to be cleverer than it.
 * - **The code lives no longer than the countdown.** On expiry it is dropped from
 *   state; it is never put in the URL, never copied to the clipboard, never
 *   printed, and never sent to analytics or error reporting. Only the QR ever
 *   carries it, and only while it can still be used.
 * - **Nothing is discarded from the mint response.** A mint hands back the only
 *   copy of a credential, so if the QR cannot be rendered the user is told
 *   *and* still offered a way forward — three separate E4 review findings were
 *   this one cause.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { MobileJoinQr } from '@/components/settings/MobileJoinQr';
import { api, type ApiError } from '@/lib/api';

interface MobileLoginCodeResponse {
  expiresAt: string;
  mobileLoginUrl: string;
}

/** A minted code, held only while it is still redeemable. */
interface LiveCode {
  mobileLoginUrl: string;
  expiresAt: string;
}

/** How often the countdown re-renders. One second is what the text shows. */
const TICK_MS = 1000;

interface MobileLoginCodeSectionProps {
  /** The signed-in user's address — shown so it is obvious whose session this is. */
  email: string;
  /**
   * Reports whether a mint is in flight, so the **dialog** can refuse to close
   * while one is.
   *
   * This has to live above this component: MUI unmounts a closed dialog's
   * children, so closing mid-mint destroys this subtree — taking `mintInFlight`
   * with it. Reopening then builds a fresh `false` ref and a second mint can
   * overlap the first. Both are safe server-side (the Lua replacement guarantees
   * exactly one redeemable code), but the two mints can reach Redis in either
   * order, so the screen can end up showing code B while code A is the live one —
   * and the user scans a code that is already dead.
   *
   * Worse, and simpler: closing mid-mint silently *discards the whole response*.
   * The code was minted, it is live for 90 seconds, and nothing ever displayed
   * it. That is the one rule this slice inherited from E4 — no branch of a
   * mint-then-show flow may throw away the only copy of a credential.
   */
  onBusyChange?: (busy: boolean) => void;
  /**
   * Whether this account proves presence through Paya ID rather than a password
   * (#1763).
   *
   * The step-up is mandatory either way; only the proof differs. An account with
   * no OS Buddy password had no proof it could give at all, so it could not
   * enrol a phone — the route answered 401 and blamed the session.
   */
  viaPayaId?: boolean;
}

export function MobileLoginCodeSection({
  email,
  onBusyChange,
  viaPayaId = false,
}: MobileLoginCodeSectionProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const [isPrompting, setIsPrompting] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isMinting, setIsMinting] = useState(false);
  const [code, setCode] = useState<LiveCode | null>(null);
  const [expired, setExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);

  /**
   * In-flight guard as a **ref**, not the `isMinting` state.
   *
   * Two rapid clicks both read the same stale `useState` value and both fire, and
   * a mint is a rotating action: the second one invalidates the first, so an
   * out-of-order resolution leaves the screen showing an already-dead code. A ref
   * is written synchronously, so the second click sees it (#1244 E4 finding 2).
   */
  const mintInFlight = useRef(false);

  /** Drop the code from state the moment it stops being usable. */
  const discard = useCallback(() => {
    setCode(null);
    setSecondsLeft(0);
    setTotalSeconds(0);
  }, []);

  const reset = useCallback(() => {
    setIsPrompting(false);
    setPassword('');
    setError(null);
    setExpired(false);
    discard();
  }, [discard]);

  // The countdown. Recomputed from `expiresAt` rather than decremented, so a
  // backgrounded tab that missed its ticks still shows the truth when it returns
  // — and so a clock that jumps cannot leave a dead code on screen.
  useEffect(() => {
    if (!code) return;

    const deadline = new Date(code.expiresAt).getTime();
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        // Auto-hide: the credential goes out of memory, not just off screen.
        setExpired(true);
        discard();
      }
    };

    tick();
    const timer = setInterval(tick, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [code, discard]);

  /**
   * Spend a step-up the browser just came back with (#1763).
   *
   * The re-authentication is a full page navigation to Paya ID and back, so the
   * dialog that started it no longer exists — the grant arrives in the URL
   * fragment instead, and `Header` reopens the dialog when it sees one.
   *
   * The fragment is scrubbed *before* the request, not after: a reload with the
   * id still in the URL would spend a grant the user did not ask to spend, and
   * the browser keeps fragments across reloads.
   */
  const stepUpConsumed = useRef(false);
  useEffect(() => {
    if (stepUpConsumed.current) return;
    const match = /^#stepUp=(.+)$/.exec(window.location.hash);
    const stepUpId = match?.[1];
    if (stepUpId === undefined) return;

    stepUpConsumed.current = true;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    mint({ stepUpId });
    // Once, on mount, whatever else changes: the fragment is gone after the
    // first pass and re-running would mint a second code nobody asked for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mint = (credential: { password: string } | { stepUpId: string }) => {
    // Synchronous, before any await: this is what makes a double click a no-op.
    if (mintInFlight.current) return;
    if ('password' in credential && !credential.password) {
      setError(t('mobileLoginCode.passwordRequired'));
      return;
    }
    mintInFlight.current = true;
    setIsMinting(true);
    onBusyChange?.(true);
    setError(null);
    setExpired(false);

    api
      .post<MobileLoginCodeResponse>('/auth/mobile-login-code', credential)
      .then((response) => {
        // The whole response, or nothing: dropping either field here would
        // strand the user holding a credential they cannot see or time.
        setCode({ mobileLoginUrl: response.mobileLoginUrl, expiresAt: response.expiresAt });
        setTotalSeconds(
          Math.max(1, Math.ceil((new Date(response.expiresAt).getTime() - Date.now()) / 1000)),
        );
        // The password has done its job; it must not sit in a form field behind a
        // live QR on a shared screen.
        setPassword('');
        setIsPrompting(false);
      })
      // `.catch` as well as `.finally`: an unhandled rejection would leave the
      // button disabled forever (#1244 E4 finding 2).
      .catch((caught: unknown) => {
        const apiError = caught as ApiError;
        // 403, not 401. A failed step-up is "your password", not "your token" —
        // and the distinction is not cosmetic: `lib/api` refreshes and *replays*
        // any 401, so a 401 here would spend two of the user's five shared
        // login attempts per typo. See the route's own comment, and
        // `mobileLoginCodeRequest.test.ts`, which pins one-submission-one-request
        // through the real client.
        if (apiError.status === 403) {
          setError(t('mobileLoginCode.wrongPassword'));
        } else if (apiError.status === 429) {
          setError(t('mobileLoginCode.tooManyAttempts'));
        } else {
          setError(tc('unknownError'));
        }
        setPassword('');
      })
      .finally(() => {
        mintInFlight.current = false;
        setIsMinting(false);
        onBusyChange?.(false);
      });
  };

  /**
   * Never leave the dialog wedged shut. If this subtree goes away for any reason
   * other than a close we blocked — a route change, a parent re-key — the busy
   * flag it set must not outlive it, or the dialog becomes unclosable.
   */
  useEffect(
    () => () => {
      onBusyChange?.(false);
    },
    [onBusyChange],
  );

  const countdownLabel = t('mobileLoginCode.expiresIn', { seconds: secondsLeft });

  return (
    <Box>
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          {t('mobileLoginCode.heading')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {t('mobileLoginCode.description', { app: tc('appName') })}
        </Typography>

        {/*
          Nothing is minted until this button is pressed, and pressing it only
          opens the password form — the credential is two deliberate acts away
          from being on screen.
        */}
        {!isPrompting && !code && (
          <Box>
            <Button
              variant="outlined"
              startIcon={<PhoneIphoneIcon />}
              onClick={() => {
                setError(null);
                setExpired(false);
                if (viaPayaId) {
                  // Whole-window navigation, not fetch: the proof has to happen
                  // in Paya ID's own page, where MFA and the password manager
                  // work. `return` brings the browser back to this exact view.
                  const back = window.location.pathname + window.location.search;
                  window.location.assign(
                    `/api/v1/auth/oidc/start?intent=step-up&return=${encodeURIComponent(back)}`,
                  );
                  return;
                }
                setIsPrompting(true);
              }}
            >
              {t('mobileLoginCode.reveal')}
            </Button>
          </Box>
        )}

        {expired && !code && (
          <Alert severity="info">{t('mobileLoginCode.expiredNote')}</Alert>
        )}

        {isPrompting && !code && (
          <Box
            component="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              mint({ password });
            }}
          >
            <Stack spacing={2}>
              <TextField
                label={t('mobileLoginCode.passwordLabel')}
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                error={!!error}
                helperText={error ?? t('mobileLoginCode.passwordHint')}
                fullWidth
                autoComplete="current-password"
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={reset} disabled={isMinting}>
                  {tc('cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={isMinting}>
                  {isMinting ? <CircularProgress size={20} /> : t('mobileLoginCode.showCode')}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {code && (
          <Stack spacing={1.5} alignItems="center">
            <MobileJoinQr
              mobileJoinUrl={code.mobileLoginUrl}
              email={email}
              expiresAt={code.expiresAt}
              heading={t('mobileLoginCode.qrHeading')}
              // A live countdown, not a date: this code dies in 90 seconds.
              caption={
                <Box sx={{ width: '100%' }}>
                  <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                    {countdownLabel}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={totalSeconds > 0 ? (secondsLeft / totalSeconds) * 100 : 0}
                    aria-label={countdownLabel}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              }
              // Never: paper outlives the code by orders of magnitude.
              enablePrint={false}
              // Copy, though. Minting this while reading the portal *on the phone*
              // is a normal thing to do, and there is no second device to scan the
              // code with — so without copy-and-paste the feature has no path at
              // all on the one device the user is holding. The clipboard window is
              // 90 seconds, for a code the user just minted for themselves behind a
              // password step-up; the invitation beside it allows copy for seven
              // days.
              enableCopy
              // The default hint is worded for a link. This is not one.
              copyHelperText={t('mobileLoginCode.copyHint', { app: tc('appName') })}
            />
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {t('mobileLoginCode.singleUseNote')}
            </Typography>
            <Button size="small" onClick={reset}>
              {t('mobileLoginCode.hide')}
            </Button>
          </Stack>
        )}

        {/*
          Re-minting goes back through the password prompt rather than reusing
          what was typed. That is not friction for its own sake: the server
          requires the step-up on every mint, so a "Get a new code" button that
          skipped it would only produce a 401 the user could not act on.
        */}
        {expired && (
          <Box>
            <Button
              variant="contained"
              onClick={() => {
                setIsPrompting(true);
                setExpired(false);
                setError(null);
              }}
            >
              {t('mobileLoginCode.getNewCode')}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
